import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../config/api';

export const BasicLayout: React.FC = () => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [leasesExpanded, setLeasesExpanded] = useState(location.pathname.startsWith('/lease-qa') || location.pathname.startsWith('/current-leases') || location.pathname.startsWith('/lease-templates'));
  const [psExpanded, setPsExpanded] = useState(location.pathname.startsWith('/pocketsmith'));
  const [unmatchedCount, setUnmatchedCount] = useState<number | null>(null);

  useEffect(() => {
    api.get('/pocketsmith/summary').then(res => {
      if (res.data?.unmatched != null) setUnmatchedCount(res.data.unmatched);
    }).catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;
  const isLeasePath = location.pathname.startsWith('/lease-qa') || location.pathname.startsWith('/current-leases') || location.pathname.startsWith('/lease-templates');
  const isPsPath = location.pathname.startsWith('/pocketsmith');
  const isGroupActive = (group: string) => isPsPath && location.search.includes(`group=${group}`);

  const linkClasses = (path: string) =>
    `block px-4 py-3 rounded-md mb-0.5 font-mono text-sm transition-colors ${
      isActive(path)
        ? 'text-white bg-gray-800 border-l-2 border-cyan-400 pl-3.5'
        : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
    }`;

  const subLinkClasses = (path: string, active?: boolean) => {
    const isOn = active || isActive(path);
    return `block py-2 px-4 pl-9 rounded-md mb-0.5 font-mono text-sm transition-colors ${
      isOn
        ? 'text-white bg-gray-800/70'
        : 'text-gray-600 hover:text-gray-400 hover:bg-gray-800/30'
    }`;
  };

  const groupLinkClasses = (group: string, color: string) => {
    const active = isGroupActive(group);
    return `block py-1.5 px-4 pl-11 rounded-md mb-0.5 font-mono text-xs transition-colors ${
      active
        ? `${color} bg-gray-800/70`
        : 'text-gray-600 hover:text-gray-400 hover:bg-gray-800/30'
    }`;
  };

  return (
    <div className="flex h-screen m-0 p-0 bg-[#111]">
      {/* Sidebar */}
      <div className="w-[250px] bg-gray-900 p-5 flex flex-col overflow-y-auto">
        <h2 className="text-white font-mono text-base font-bold mt-0 mb-0">
          <span className="text-cyan-400">~/</span>expense-tracker
          <span className="text-gray-600 text-xs font-normal ml-2">v5</span>
        </h2>

        <nav className="mt-8 flex-1">
          {/* Main Navigation */}
          <Link to="/" className={linkClasses('/')}>Dashboard</Link>
          <Link to="/documents" className={linkClasses('/documents')}>Documents</Link>
          <Link to="/properties" className={linkClasses('/properties')}>Properties</Link>
          <Link to="/expenses" className={linkClasses('/expenses')}>Expenses</Link>
          <Link to="/subscriptions" className={linkClasses('/subscriptions')}>Subscriptions</Link>
          <Link to="/tech-stack" className={linkClasses('/tech-stack')}>Tech Stack</Link>
          <Link to="/hoa" className={linkClasses('/hoa')}>HOA Payments</Link>
          <Link to="/hoa-portal" className={linkClasses('/hoa-portal')}>HOA Portal</Link>
          <Link to="/hoa-status" className={linkClasses('/hoa-status')}>HOA Status</Link>
          {/* Leases Section */}
          <div
            onClick={() => setLeasesExpanded(!leasesExpanded)}
            className={`flex justify-between items-center px-4 py-3 rounded-md mb-0.5 cursor-pointer select-none font-mono text-sm transition-colors ${
              isLeasePath ? 'text-white bg-gray-800' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <span>Leases</span>
            <span className="text-gray-700 text-[10px]">{leasesExpanded ? 'v' : '>'}</span>
          </div>
          {leasesExpanded && (
            <>
              <Link to="/lease-qa" className={subLinkClasses('/lease-qa')}>Ask Questions</Link>
              <Link to="/current-leases" className={subLinkClasses('/current-leases')}>Current Leases</Link>
              <Link to="/lease-templates" className={subLinkClasses('/lease-templates')}>Templates</Link>
            </>
          )}
          <Link to="/vendors" className={linkClasses('/vendors')}>Vendors</Link>

          {/* PocketSmith Section */}
          <div className="mt-6 mb-4 border-t border-gray-800 pt-4">
            <div
              onClick={() => setPsExpanded(!psExpanded)}
              className={`flex justify-between items-center px-1 cursor-pointer select-none text-xs uppercase tracking-wider font-mono mb-2 ${
                isPsPath ? 'text-cyan-400' : 'text-gray-600 hover:text-gray-400'
              }`}
            >
              <span>PocketSmith</span>
              <span className="text-gray-700 text-[10px]">{psExpanded ? 'v' : '>'}</span>
            </div>
            {psExpanded && (
              <>
                <Link
                  to="/pocketsmith"
                  className={`${subLinkClasses('/pocketsmith', isActive('/pocketsmith') && !location.search)} flex justify-between items-center`}
                >
                  <span>All Transactions</span>
                  {unmatchedCount != null && unmatchedCount > 0 && (
                    <span className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                      {unmatchedCount}
                    </span>
                  )}
                </Link>
                <Link to="/pocketsmith?group=PERSONAL" className={groupLinkClasses('PERSONAL', 'text-amber-400')}>
                  <span className="mr-1.5">&#9679;</span>Personal
                </Link>
                <Link to="/pocketsmith?group=BUSINESS" className={groupLinkClasses('BUSINESS', 'text-blue-400')}>
                  <span className="mr-1.5">&#9679;</span>Business
                </Link>
                <Link to="/pocketsmith?group=INVESTMENT" className={groupLinkClasses('INVESTMENT', 'text-emerald-400')}>
                  <span className="mr-1.5">&#9679;</span>Investment
                </Link>
                <Link to="/pocketsmith/rules" className={subLinkClasses('/pocketsmith/rules')}>Auto-Match Rules</Link>
                <Link to="/pocketsmith/accounts" className={subLinkClasses('/pocketsmith/accounts')}>Accounts</Link>
                <Link to="/pocketsmith/analytics" className={subLinkClasses('/pocketsmith/analytics')}>Analytics</Link>
                <Link to="/pocketsmith/property-spending" className={subLinkClasses('/pocketsmith/property-spending')}>Property Spending</Link>
                <Link to="/ledger" className={subLinkClasses('/ledger')}>Reconciled Ledger</Link>
              </>
            )}
          </div>

          {/* Settings Section */}
          <div className="mt-6 mb-4 border-t border-gray-800 pt-4">
            <div className="text-gray-600 text-xs uppercase tracking-wider font-mono mb-2 px-1">Settings</div>
            <Link to="/tags" className={linkClasses('/tags')}>Tags & Settings</Link>
            <Link to="/email-status" className={linkClasses('/email-status')}>Email Status</Link>
            <Link to="/email-intelligence" className={linkClasses('/email-intelligence')}>Email Intelligence</Link>
            {(user?.role === 'ADMIN' || user?.role === 'admin') && (
              <>
                <Link to="/admin/users" className={linkClasses('/admin/users')}>Admin Users</Link>
                <Link to="/admin/system-status" className={linkClasses('/admin/system-status')}>System Status</Link>
              </>
            )}
          </div>

          {/* Coming Soon Section */}
          <div className="mt-6 mb-4 border-t border-gray-800 pt-4">
            <div className="text-gray-600 text-xs uppercase tracking-wider font-mono mb-2 px-1">Coming Soon</div>
            <div className="text-gray-700 text-sm font-mono py-2 px-4">OpenPhone Integration</div>
            <div className="text-gray-700 text-sm font-mono py-2 px-4">Mobile App</div>
          </div>
        </nav>

        <button
          onClick={handleLogout}
          className="text-gray-600 hover:text-gray-400 font-mono text-sm bg-transparent border-none cursor-pointer mt-4 text-left transition-colors"
        >
          Logout
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 pb-5 overflow-y-auto bg-[#111] relative">
        <Outlet />
      </div>
    </div>
  );
};
